import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { TCODES } from "@/constants/tcodes";
import { KeyRound, Search, ArrowUpRight, Info } from "lucide-react";

export default function TransactionCodes() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const filtered = TCODES.filter(
      (t) => !term || t.code.toLowerCase().includes(term) || t.name.toLowerCase().includes(term) ||
        t.module.toLowerCase().includes(term) || (t.sap || "").toLowerCase().includes(term));
    const map = {};
    filtered.forEach((t) => { (map[t.module] = map[t.module] || []).push(t); });
    return Object.entries(map);
  }, [q]);

  return (
    <div data-testid="tcodes-page">
      <PageHeader
        title="Transaction Codes"
        subtitle="EP Group runs its own proprietary transaction codes (EP-<module><nn>) so they never clash with SAP S/4HANA standard codes. The S/4HANA equivalent is shown for reference and training only — the system uses our own codes."
        icon={KeyRound}
        accent="#354a5f"
      >
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9aa0a6]" />
          <input
            data-testid="tcode-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search code, name, module or S/4 code…"
            className="w-72 rounded-lg border border-[#e5e9f0] bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-[#0a6ed1]"
          />
        </div>
      </PageHeader>

      <div className="mb-5 flex items-start gap-2 rounded-lg border border-[#d6e4ff] bg-[#eff5ff] px-4 py-3 text-xs text-[#334155]">
        <Info size={15} className="mt-0.5 shrink-0 text-[#0a6ed1]" />
        <span>Codes are proprietary to EP Group. Any resemblance to SAP naming is for staff transitioning from S/4HANA — the platform is independent and the S/4 column is a cross-reference, not a live SAP link.</span>
      </div>

      {groups.length === 0 && (
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-8 text-center text-sm text-[#6a6d70]">No transaction codes match “{q}”.</div>
      )}

      <div className="space-y-6">
        {groups.map(([module, rows]) => (
          <div key={module} className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-[#e5e9f0] bg-[#f7f8fa] px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: rows[0].color }} />
              <span className="text-sm font-semibold uppercase tracking-wide text-[#32363a]">{module}</span>
              <span className="text-[11px] text-[#9aa0a6]">{rows.length} code{rows.length > 1 ? "s" : ""}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
                  <tr className="border-b border-[#eef1f5]">
                    <th className="px-4 py-2">EP Code</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2">S/4HANA Equivalent</th>
                    <th className="px-4 py-2 text-right">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.code} data-testid={`tcode-row-${t.code}`} className="border-b border-[#f2f4f7] last:border-0 hover:bg-[#f7f8fa]">
                      <td className="px-4 py-2.5">
                        <span className="rounded-md px-2 py-1 font-mono text-xs font-semibold" style={{ background: `${t.color}18`, color: t.color }}>{t.code}</span>
                      </td>
                      <td className="px-4 py-2.5 text-[#32363a]">{t.name}</td>
                      <td className="px-4 py-2.5"><span className="font-mono text-xs text-[#9aa0a6]">{t.sap}</span></td>
                      <td className="px-4 py-2.5 text-right">
                        <button data-testid={`tcode-open-${t.code}`} onClick={() => navigate(t.path)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2.5 py-1 text-[11px] font-medium text-[#0a6ed1] hover:border-[#0a6ed1]">
                          Open <ArrowUpRight size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
